import { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";

const SwissFlag = () => (
  <svg
    viewBox="0 0 32 32"
    className="inline-block w-3 h-3 align-[-2px] ml-1 rounded-[2px]"
    aria-label="Svizzera"
    data-testid="swiss-flag"
  >
    <rect width="32" height="32" fill="#DA291C" />
    <rect x="13" y="7" width="6" height="18" fill="#FFFFFF" />
    <rect x="7" y="13" width="18" height="6" fill="#FFFFFF" />
  </svg>
);

const NAV = [
  { label: "Strumenti", to: "/#servizi", testid: "nav-strumenti" },
  { label: "Profilo e approccio", to: "/profilo", testid: "nav-profilo" },
  { label: "Contatti", to: "/contatti", testid: "nav-contatti" },
];

export default function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-dvh bg-white flex flex-col">
      <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-5 min-h-[60px] py-2 flex items-center justify-between gap-3">
          <Link to="/" data-testid="header-home-link" className="shrink-0 leading-tight">
            <span className="block font-display text-lg font-semibold tracking-tight text-[#142D43]">
              Nicolò Pipitone
            </span>
            <span className="block text-[11px] text-[#374151]">
              Psichiatra e Psicoterapeuta FMH<SwissFlag />
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6" data-testid="header-nav" aria-label="Navigazione principale">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className="text-[15px] text-[#374151] hover:text-[#142D43] transition-colors duration-150"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/feedback"
              data-testid="header-area-riservata"
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-[10px] text-sm font-semibold bg-[#142D43] text-white hover:bg-[#1E3F5C] transition-colors duration-150"
            >
              Area riservata
            </Link>
          </nav>

          <button
            type="button"
            data-testid="header-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className="md:hidden inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-[10px] text-[15px] font-semibold text-[#142D43] border border-[#E5E7EB] hover:border-[#142D43] transition-colors duration-150"
          >
            {menuOpen ? <X size={19} /> : <Menu size={19} />}
            Menu
          </button>
        </div>

        {menuOpen && (
          <nav id="mobile-menu" data-testid="mobile-menu" className="md:hidden border-t border-[#E5E7EB] bg-white" aria-label="Menu mobile">
            <div className="max-w-4xl mx-auto px-5 py-2 flex flex-col">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-${item.testid}`}
                  className="min-h-[48px] flex items-center text-base text-[#142D43] border-b border-[#F4F6F8]"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/feedback"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-area-riservata"
                className="min-h-[48px] flex items-center text-base font-semibold text-[#142D43] border-b border-[#F4F6F8]"
              >
                Area riservata
              </Link>
              <Link
                to="/appuntamento"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-cta"
                className="my-3 inline-flex items-center justify-center min-h-[48px] px-4 rounded-[10px] text-base font-semibold bg-[#142D43] text-white hover:bg-[#1E3F5C] transition-colors duration-150"
              >
                Richiedi un colloquio
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main className="relative flex-1 flex flex-col">
        <Outlet />
      </main>

      <footer className="border-t border-[#E5E7EB] bg-white" data-testid="footer">
        <div className="max-w-4xl mx-auto px-5 py-10">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-8">
            <div>
              <p className="font-display text-lg font-semibold tracking-tight text-[#142D43]">Dr. med. Nicolò Pipitone</p>
              <p className="mt-1 text-sm text-[#374151]">
                Psichiatra e Psicoterapeuta FMH<SwissFlag /> · Lugano-Paradiso
              </p>
              <a
                href="https://www.instagram.com/drpipitone"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 min-h-[44px] text-sm text-[#374151] hover:text-[#142D43] transition-colors duration-150"
                data-testid="footer-instagram"
              >
                <Instagram size={16} /> @drpipitone
              </a>
            </div>
            <nav aria-label="Collegamenti" data-testid="footer-nav" className="flex flex-col gap-1 text-sm">
              <Link to="/#servizi" className="min-h-[36px] flex items-center text-[#374151] hover:text-[#142D43] transition-colors">Strumenti dello Studio</Link>
              <Link to="/profilo" className="min-h-[36px] flex items-center text-[#374151] hover:text-[#142D43] transition-colors">Profilo e approccio</Link>
              <Link to="/contatti" className="min-h-[36px] flex items-center text-[#374151] hover:text-[#142D43] transition-colors" data-testid="footer-legal">Contatti e privacy</Link>
              <Link to="/feedback" className="min-h-[36px] flex items-center font-semibold text-[#142D43] hover:text-[#1E3F5C] transition-colors" data-testid="footer-area-riservata">Area riservata</Link>
            </nav>
          </div>
          <div className="mt-8 pt-5 border-t border-[#E5E7EB] text-xs text-[#6B7280]">
            © 2026 Dr. med. Nicolò Pipitone — Tutti i diritti riservati
          </div>
        </div>
      </footer>
    </div>
  );
}
